import {
  useContext,
  createContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import { getAssistants } from "V2.0/HomePages/Assistants/service";

type Assistant = {
  name: string;
  description: string;
  _id: string;
  assistantUsage: object;
};

interface AssistantsContextValue {
  assistants: Array<Assistant>;
  fetchAssistants: (projectId: string, loading?: boolean) => void;
  isAssistantsLoading: boolean;
}

const AssistantsContext = createContext<AssistantsContextValue>({
  assistants: [],
  fetchAssistants: (projectId: string, loading?: boolean) => {},
  isAssistantsLoading: true,
});

export function useAssistantsContext() {
  return useContext(AssistantsContext);
}

export default function AssistantsProvider(props) {
  const { children } = props;

  const [state, setState] = useState({
    assistants: [],
    isLoading: true,
  });

  const setIsLoading = useCallback((loading) => {
    setState((prevState) => {
      return {
        ...prevState,
        isLoading: loading,
      };
    });
  }, []);

  const fetchAssistants = useCallback(
    async (workspaceId, loading = true) => {
      setIsLoading(loading);
      try {
        const response = await getAssistants({ projectId: workspaceId });

        const { data, status } = response;

        if (status === 200) {
          setState({
            assistants: data.assistants,
            isLoading: false,
          });
        }
      } catch (e) {
        setIsLoading(false);
      }
    },
    [setState, setIsLoading]
  );

  const value = useMemo(() => {
    return {
      assistants: state.assistants,
      isAssistantsLoading: state.isLoading,
      fetchAssistants,
    };
  }, [state, fetchAssistants]);

  return (
    <AssistantsContext.Provider value={value}>
      {children}
    </AssistantsContext.Provider>
  );
}
